/**
 * Host photos on CloudFront under /venue/{venueId}/{filename}.
 * Dashboard edits filename only; full URL is composed per venue.
 */
export const HOST_PHOTO_CDN_BASE = 'https://d1ah1witb1rydn.cloudfront.net';

/**
 * Value for settings form: filename (or path under venue) from API full URL or stored filename.
 * @param {string} [stored]
 * @param {string} [venueId]
 * @returns {string}
 */
export function hostPhotoFilenameFromStoredUrl(stored, venueId) {
  if (stored == null || stored === '') return '';
  const s = String(stored).trim();
  if (!s) return '';

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      let path = u.pathname.replace(/^\/+/, '');
      if (venueId) {
        const prefix = `venue/${venueId}/`;
        if (path.toLowerCase().startsWith(prefix.toLowerCase())) {
          return path.slice(prefix.length);
        }
      }
      const parts = path.split('/').filter(Boolean);
      return parts[parts.length - 1] || '';
    } catch {
      return s;
    }
  }

  let f = s.replace(/^\/+/, '');
  if (venueId) {
    const prefix = `venue/${venueId}/`;
    if (f.toLowerCase().startsWith(prefix.toLowerCase())) {
      f = f.slice(prefix.length);
    }
  }
  return f;
}

/**
 * Full URL for API payload and previews from form value (filename or legacy full URL on our CDN).
 * External absolute URLs are returned unchanged.
 * @param {string} [input]
 * @param {string} [venueId]
 * @returns {string|null}
 */
export function hostPhotoFullUrlFromUserInput(input, venueId) {
  if (!venueId) return null;
  const raw = String(input ?? '').trim();
  if (!raw) return null;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const cfOrigin = new URL(HOST_PHOTO_CDN_BASE).origin;
      if (u.origin === cfOrigin) {
        const name = hostPhotoFilenameFromStoredUrl(raw, venueId);
        if (name) {
          const path = name
            .split('/')
            .filter(Boolean)
            .map((seg) => encodeURIComponent(seg))
            .join('/');
          return `${HOST_PHOTO_CDN_BASE}/venue/${venueId}/${path}`;
        }
      }
    } catch {
      return raw;
    }
    return raw;
  }

  let f = raw.replace(/^\/+/, '');
  const prefix = `venue/${venueId}/`;
  if (f.toLowerCase().startsWith(prefix.toLowerCase())) {
    f = f.slice(prefix.length);
  }
  const path = f
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
  return `${HOST_PHOTO_CDN_BASE}/venue/${venueId}/${path}`;
}

/** Resolved URL for img src (public pages + dashboard preview). */
export function resolveHostPhotoUrl(venue) {
  if (!venue) return null;
  const raw =
    venue.host_photo_url ||
    venue.owner_photo_url ||
    venue.contact_photo_url ||
    venue.owner?.profile_image_url ||
    venue.owner?.photo_url ||
    venue.owner?.avatar_url ||
    null;
  const venueId = venue.id ?? venue.venue_id;
  return resolveStoredHostPhotoUrl(raw, {
    venueId,
    images: venue.images
  });
}

export const HOST_PHOTO_UPLOAD_BASENAME = 'host-photo';

const HOST_PHOTO_UUID_PREFIX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_host-photo\./i;

/** True when URL/filename is host-photo.* without the API's uuid prefix (broken / stale). */
export function isBareHostPhotoUrl(url) {
  if (!url) return false;
  const value = String(url).trim();
  return (
    /(?:^|\/)host-photo\.(jpe?g|png|webp)(\?|$)/i.test(value) &&
    !HOST_PHOTO_UUID_PREFIX.test(value)
  );
}

export function findGalleryHostPhotoUrl(images, preferredUrl = null) {
  if (!Array.isArray(images)) return null;
  const hostImages = images.filter((img) => isHostPhotoGalleryImageRecord(img));
  if (hostImages.length === 0) return null;

  const normalize = (value) =>
    String(value ?? '').split('?')[0].replace(/\/+$/, '').toLowerCase();

  const preferred = String(preferredUrl ?? '').trim();
  if (preferred) {
    const prefKey = normalize(preferred);
    const matched = hostImages.find((img) => {
      const url = img?.image_url || img?.url || img?.imageUrl;
      return url && normalize(url) === prefKey;
    });
    if (matched) {
      return matched.image_url || matched.url || matched.imageUrl || null;
    }
  }

  const sorted = [...hostImages].sort((a, b) => {
    const aTime = new Date(a?.uploaded_at || a?.created_at || 0).getTime();
    const bTime = new Date(b?.uploaded_at || b?.created_at || 0).getTime();
    return bTime - aTime;
  });
  const latest = sorted[0];
  return latest?.image_url || latest?.url || latest?.imageUrl || null;
}

/**
 * Resolve host photo for img src. Gallery uploads land as {uuid}_host-photo.ext on the CDN;
 * bare host-photo.ext paths 403 unless we use the completed gallery URL.
 */
export function resolveStoredHostPhotoUrl(stored, { venueId, images } = {}) {
  if (stored == null || !String(stored).trim()) {
    return findGalleryHostPhotoUrl(images);
  }
  const value = String(stored).trim();
  let resolved = /^https?:\/\//i.test(value)
    ? value
    : venueId
      ? hostPhotoFullUrlFromUserInput(value, venueId)
      : value;
  if (isBareHostPhotoUrl(resolved)) {
    const galleryUrl = findGalleryHostPhotoUrl(images, resolved);
    if (galleryUrl) return galleryUrl;
  }
  return resolved;
}

export function isHostPhotoGalleryImageRecord(image, hostPhotoUrl = null) {
  const url = String(image?.image_url || image?.url || image?.imageUrl || '').toLowerCase();
  const name = String(
    image?.filename || image?.file_name || image?.original_filename || ''
  ).toLowerCase();
  const matchesHostPhotoName =
    /host-photo\.(jpe?g|png|webp)(\?|$)/i.test(url) ||
    /^host-photo\.(jpe?g|png|webp)$/i.test(name) ||
    /\/host-photo\.(jpe?g|png|webp)(\?|$)/i.test(url);
  if (matchesHostPhotoName) return true;

  const hostUrl = String(hostPhotoUrl ?? '').trim();
  if (!hostUrl || !url) return false;
  const normalize = (value) => String(value).split('?')[0].replace(/\/+$/, '').toLowerCase();
  return normalize(url) === normalize(hostUrl);
}
